import { openDB, IDBPDatabase } from 'idb';
import { Document } from './api';

const DB_NAME = 'overlook-docs';
const DB_VERSION = 1;
const DOCUMENTS_STORE = 'documents';
const METADATA_STORE = 'metadata';

let dbInstance: IDBPDatabase | null = null;

/**
 * Initialize and return the IndexedDB database
 */
export const initDB = async (): Promise<IDBPDatabase> => {
  if (dbInstance) {
    return dbInstance;
  }

  try {
    dbInstance = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Create documents store
        if (!db.objectStoreNames.contains(DOCUMENTS_STORE)) {
          const docStore = db.createObjectStore(DOCUMENTS_STORE, {
            keyPath: 'slug',
          });
          docStore.createIndex('id', 'id', { unique: true });
          docStore.createIndex('title', 'title', { unique: false });
          docStore.createIndex('folder_path', 'folder_path', { unique: false });
          docStore.createIndex('updated_at', 'updated_at', { unique: false });
        }

        // Create metadata store
        if (!db.objectStoreNames.contains(METADATA_STORE)) {
          db.createObjectStore(METADATA_STORE, {
            keyPath: 'key',
          });
        }
      },
    });

    console.log('[DB] IndexedDB initialized');
    return dbInstance;
  } catch (error) {
    console.error('[DB] Failed to initialize IndexedDB:', error);
    throw error;
  }
};

/**
 * Save a single document to IndexedDB
 */
export const saveDocument = async (doc: Document): Promise<void> => {
  try {
    const db = await initDB();
    await db.put(DOCUMENTS_STORE, doc);
    console.log(`[DB] Saved document: ${doc.slug}`);
  } catch (error) {
    console.error('[DB] Failed to save document:', error);
    throw error;
  }
};

/**
 * Save multiple documents to IndexedDB (batch operation)
 * This REPLACES the entire document cache
 */
export const saveDocuments = async (docs: Document[]): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(DOCUMENTS_STORE, 'readwrite');
    const store = tx.objectStore(DOCUMENTS_STORE);

    // Clear existing documents first
    await store.clear();
    
    // Then add new documents
    await Promise.all(docs.map((doc) => store.put(doc)));
    await tx.done;

    console.log(`[DB] Replaced cache with ${docs.length} documents`);
  } catch (error) {
    console.error('[DB] Failed to save documents:', error);
    throw error;
  }
};

/**
 * Get a single document by slug
 */
export const getDocument = async (slug: string): Promise<Document | undefined> => {
  try {
    const db = await initDB();
    const doc = await db.get(DOCUMENTS_STORE, slug);
    return doc;
  } catch (error) {
    console.error('[DB] Failed to get document:', error);
    return undefined;
  }
};

/**
 * Get all documents from IndexedDB
 */
export const getAllDocuments = async (): Promise<Document[]> => {
  try {
    const db = await initDB();
    const docs = await db.getAll(DOCUMENTS_STORE);
    console.log(`[DB] Retrieved ${docs.length} documents from cache`);
    return docs;
  } catch (error) {
    console.error('[DB] Failed to get all documents:', error);
    return [];
  }
};

/**
 * Get cached folders list
 */
export const getFolders = async (): Promise<string[]> => {
  try {
    const db = await initDB();
    const entry = await db.get(METADATA_STORE, 'folders');
    return entry?.value || [];
  } catch (error) {
    console.error('[DB] Failed to get folders:', error);
    return [];
  }
};

/**
 * Save folders list to cache
 */
export const saveFolders = async (folders: string[]): Promise<void> => {
  try {
    const db = await initDB();
    await db.put(METADATA_STORE, { key: 'folders', value: folders });
    console.log(`[DB] Saved ${folders.length} folders`);
  } catch (error) {
    console.error('[DB] Failed to save folders:', error);
    throw error;
  }
};

/**
 * Get cached tags list
 */
export const getTags = async (): Promise<string[]> => {
  try {
    const db = await initDB();
    const entry = await db.get(METADATA_STORE, 'tags');
    return entry?.value || [];
  } catch (error) {
    console.error('[DB] Failed to get tags:', error);
    return [];
  }
};

/**
 * Save tags list to cache
 */
export const saveTags = async (tags: string[]): Promise<void> => {
  try {
    const db = await initDB();
    await db.put(METADATA_STORE, { key: 'tags', value: tags });
    console.log(`[DB] Saved ${tags.length} tags`);
  } catch (error) {
    console.error('[DB] Failed to save tags:', error);
    throw error;
  }
};

/**
 * Get last sync timestamp
 */
export const getLastSync = async (): Promise<Date | null> => {
  try {
    const db = await initDB();
    const entry = await db.get(METADATA_STORE, 'lastSync');
    return entry?.value ? new Date(entry.value) : null;
  } catch (error) {
    console.error('[DB] Failed to get last sync:', error);
    return null;
  }
};

/**
 * Update last sync timestamp
 */
export const setLastSync = async (date: Date): Promise<void> => {
  try {
    const db = await initDB();
    await db.put(METADATA_STORE, { key: 'lastSync', value: date.toISOString() });
    console.log(`[DB] Updated last sync: ${date.toISOString()}`);
  } catch (error) {
    console.error('[DB] Failed to set last sync:', error);
    throw error;
  }
};

/**
 * Clear all documents from IndexedDB
 */
export const clearAllDocuments = async (): Promise<void> => {
  try {
    const db = await initDB();
    await db.clear(DOCUMENTS_STORE);
    console.log('[DB] Cleared all documents');
  } catch (error) {
    console.error('[DB] Failed to clear documents:', error);
    throw error;
  }
};

/**
 * Check storage quota and usage
 */
export const checkStorageQuota = async (): Promise<{
  usage: number;
  quota: number;
  percentUsed: number;
}> => {
  try {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const percentUsed = quota > 0 ? (usage / quota) * 100 : 0;

      console.log(
        `[DB] Storage: ${(usage / 1024 / 1024).toFixed(2)}MB / ${(
          quota /
          1024 /
          1024
        ).toFixed(2)}MB (${percentUsed.toFixed(1)}%)`
      );

      return { usage, quota, percentUsed };
    } else {
      console.warn('[DB] Storage API not available');
      return { usage: 0, quota: 0, percentUsed: 0 };
    }
  } catch (error) {
    console.error('[DB] Failed to check storage quota:', error);
    return { usage: 0, quota: 0, percentUsed: 0 };
  }
};
